import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  CheckCircle,
  Error,
} from '@mui/material';
import { 
  Payment as PaymentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Home as HomeIcon,
  InfoOutlined,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

const PaymentPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);

  // 获取URL参数
  const sessionId = searchParams.get('session_id');
  const success = searchParams.get('success');
  const cancelled = searchParams.get('cancelled');

  useEffect(() => {
    const processPaymentResult = async () => {
      setLoading(true);
      
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      if (success === 'true' || sessionId) {
        setPaymentStatus('success');
      } else if (cancelled === 'true') {
        setPaymentStatus('cancelled');
      } else {
        setPaymentStatus('failed');
      }
      
      setLoading(false);
    };

    processPaymentResult();
  }, [sessionId, success, cancelled]);

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleViewPayments = () => {
    navigate('/payments/history');
  };

  const handleTryAgain = () => {
    navigate('/professionals');
  };

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CircularProgress size={60} sx={{ mb: 3 }} />
            <Typography variant="h6" gutterBottom>
              处理支付结果...
            </Typography>
            <Typography variant="body2" color="textSecondary">
              请稍候，我们正在验证您的支付信息
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  const renderPaymentSuccess = () => (
    <Card sx={{ textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
      <CardContent sx={{ py: 6 }}>
        <CheckCircleIcon sx={{ fontSize: 80, mb: 3 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          支付成功！
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          感谢您的支付，您的 Coffee Chat 会话已确认
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            sx={{ 
              bgcolor: 'white', 
              color: 'success.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
          >
            返回首页
          </Button>
          <Button
            variant="outlined"
            sx={{ 
              borderColor: 'white', 
              color: 'white',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            startIcon={<ReceiptIcon />}
            onClick={handleViewPayments}
          >
            查看支付记录
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderPaymentCancelled = () => (
    <Card sx={{ textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
      <CardContent sx={{ py: 6 }}>
        <InfoOutlined sx={{ fontSize: 80, mb: 3 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          支付已取消
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          您已取消支付，您可以稍后再试
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            sx={{ 
              bgcolor: 'white', 
              color: 'warning.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
            startIcon={<PaymentIcon />}
            onClick={handleTryAgain}
          >
            重新支付
          </Button>
          <Button
            variant="outlined"
            sx={{ 
              borderColor: 'white', 
              color: 'white',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
          >
            返回首页
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  const renderPaymentFailed = () => (
    <Card sx={{ textAlign: 'center', bgcolor: 'error.main', color: 'white' }}>
      <CardContent sx={{ py: 6 }}>
        <ErrorIcon sx={{ fontSize: 80, mb: 3 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          支付失败
        </Typography>
        <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
          很抱歉，支付过程中出现了问题
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            sx={{ 
              bgcolor: 'white', 
              color: 'error.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
            startIcon={<PaymentIcon />}
            onClick={handleTryAgain}
          >
            重新支付
          </Button>
          <Button
            variant="outlined"
            sx={{ 
              borderColor: 'white', 
              color: 'white',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' }
            }}
            startIcon={<HomeIcon />}
            onClick={handleGoHome}
          >
            返回首页
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <PaymentIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          支付结果
        </Typography>
      </Box>

      {paymentStatus === 'success' && renderPaymentSuccess()}
      {paymentStatus === 'cancelled' && renderPaymentCancelled()}
      {paymentStatus === 'failed' && renderPaymentFailed()}

      {/* 额外信息 */}
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="body2" color="textSecondary">
          如果您有任何问题，请联系我们的客服团队
        </Typography>
      </Box>
    </Container>
  );
};

export default PaymentPage; 